import { Controller } from "@hotwired/stimulus"

// Drives the unobtrusive "Add to Home Screen" banner.
//
// The banner starts hidden. We reveal it only when the browser fires
// `beforeinstallprompt` (Chrome / Edge / Android) AND the operator hasn't
// dismissed it within the last `dismissDays`. Clicking "Add" triggers the
// native install prompt; dismissing remembers the choice in localStorage so the
// banner stays gone. iOS never fires the event, so the banner never shows there.
export default class extends Controller {
  static values = {
    dismissKey: String,
    dismissDays: { type: Number, default: 30 },
  }

  connect() {
    this.deferredPrompt = null
    this.onBeforeInstall = this.#beforeInstall.bind(this)
    this.onInstalled = this.#installed.bind(this)
    window.addEventListener("beforeinstallprompt", this.onBeforeInstall)
    window.addEventListener("appinstalled", this.onInstalled)
  }

  disconnect() {
    window.removeEventListener("beforeinstallprompt", this.onBeforeInstall)
    window.removeEventListener("appinstalled", this.onInstalled)
  }

  async install() {
    if (!this.deferredPrompt) return
    this.deferredPrompt.prompt()
    const { outcome } = await this.deferredPrompt.userChoice
    this.deferredPrompt = null
    if (outcome === "accepted") {
      this.element.remove()
    } else {
      this.#hide()
    }
  }

  dismiss() {
    this.#remember()
    this.element.remove()
  }

  #beforeInstall(event) {
    // Stop Chrome's own mini-infobar; we present our own affordance instead.
    event.preventDefault()
    this.deferredPrompt = event
    if (this.#dismissed) return
    this.#show()
  }

  #installed() {
    this.element.remove()
  }

  #show() {
    this.element.classList.remove("hidden")
  }

  #hide() {
    this.element.classList.add("hidden")
  }

  get #dismissed() {
    const raw = localStorage.getItem(this.#storageKey)
    if (!raw) return false
    const until = parseInt(raw, 10)
    if (Number.isNaN(until)) return true
    return Date.now() < until
  }

  #remember() {
    const until = Date.now() + this.dismissDaysValue * 24 * 60 * 60 * 1000
    localStorage.setItem(this.#storageKey, String(until))
  }

  get #storageKey() {
    return `banner-dismissed:${this.dismissKeyValue}`
  }
}
